import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

const normalizeId = (value) =>
  typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "").replace(/\/api$/i, "");
const normalizeRole = (value) => String(value || "").trim().toLowerCase().replace(/[\s_]/g, "");

// Match the AuthProviders' localStorage token precedence and API pins. Tokens
// are not read from sessionStorage. Unlike the client's in-memory fallback,
// missing browser credentials fail closed here. Persisted users only invalidate;
// they never establish a planner owner.
const authBase = (portal) => {
  const configured = portal === "client"
    ? normalizeBase(process.env.REACT_APP_BACKEND_URL)
    : normalizeBase(process.env.REACT_APP_BACKEND_URL) || normalizeBase(process.env.REACT_APP_BACKEND) || normalizeBase(process.env.REACT_APP_API_URL);
  return configured || "https://cphere-backend.onrender.com";
};
const readToken = (portal) => {
  try {
    return portal === "client"
      ? localStorage.getItem("clientAuthToken") || localStorage.getItem("authToken") || localStorage.getItem("token") || ""
      : localStorage.getItem("superadminToken") || localStorage.getItem("authToken") || "";
  } catch { return ""; }
};
const logoutActive = (portal) => {
  const flags = portal === "client"
    ? ["cortexLogoutInProgress", "cortexLogoutMode"]
    : ["cortexLogoutInProgress", "superadminLogoutIntent"];
  try {
    return flags.some((key) => localStorage.getItem(key) === "1" || sessionStorage.getItem(key) === "1");
  } catch { return true; }
};
const AUTH_STORAGE_KEYS = new Set([
  "clientAuthToken", "superadminToken", "authToken", "token", "role", "email", "userEmail",
  "cortexClientAuthState", "cortexSuperadminAuthState", "authState", "cortexLogoutInProgress",
  "cortexLogoutMode", "superadminLogoutIntent", "cortexLastLogoutTs", "superadminLastLogoutTs",
]);
const providerIdentity = (auth) => {
  const state = auth?.authState || auth || {};
  const user = state.user || auth?.user || {};
  return JSON.stringify([
    state.isAuthenticated, state.isAuthenticating, state.isLoading, auth?.isAuthenticating, auth?.isLoading,
    state.authToken, auth?.authToken, state.role, user.id, user._id, user.email,
    state.email || auth?.email, state.advertisers, state.accounts, user.advertiserIds,
  ]);
};

/**
 * Mount planner state only after a token-bound server identity and advertiser
 * access check. Revoke synchronously during a provider/route render or storage
 * event, so callbacks cannot use the previous account before effect cleanup.
 */
export default function PlannerSessionBoundary({ portal, advertiserId, campaignId, apiBase, children }) {
  const auth = useAuth();
  const providerState = auth?.authState || auth || {};
  const providerPending = providerState.isAuthenticating === true || providerState.isLoading === true ||
    auth?.isAuthenticating === true || auth?.isLoading === true;
  const providerBlocked = providerPending || providerState.isAuthenticated === false || auth?.isAuthenticated === false;
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState(null);
  const revision = useRef(0);
  const active = useRef(null);
  const requestedAdvertiser = normalizeId(advertiserId);
  const requestedCampaign = normalizeId(campaignId);
  const token = readToken(portal);
  const scope = JSON.stringify([portal, token, providerIdentity(auth), requestedAdvertiser, requestedCampaign, apiBase, refresh, logoutActive(portal)]);
  if (!active.current || active.current.scope !== scope || (active.current.revoked && token && !logoutActive(portal))) {
    if (active.current) active.current.revoked = true;
    active.current = { scope, revision: ++revision.current, revoked: false };
  }
  const epoch = active.current;

  useEffect(() => {
    const revoke = () => {
      if (active.current) active.current.revoked = true;
      setRefresh((value) => value + 1);
    };
    const onStorage = (event) => {
      if (event.key == null || AUTH_STORAGE_KEYS.has(event.key)) revoke();
    };
    const checkToken = () => {
      if (readToken(portal) !== token || logoutActive(portal)) revoke();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", checkToken);
    window.addEventListener("pageshow", checkToken);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", checkToken);
      window.removeEventListener("pageshow", checkToken);
    };
  }, [portal, token]);

  useEffect(() => {
    // StrictMode replays effects without another render. Each run gets its own
    // cancellation bit in addition to the synchronously revoked render epoch.
    let cancelled = false;
    const controller = new AbortController();
    const isCurrent = () => {
      // Once a callback observes revocation it cannot revive if an old token
      // later reappears (for example while logout/login storage is changing).
      if (readToken(portal) !== token || !token || logoutActive(portal)) epoch.revoked = true;
      return !cancelled && !providerBlocked && !epoch.revoked && active.current === epoch;
    };
    const read = async (url) => {
      if (!isCurrent()) throw new Error("Session changed");
      const response = await fetch(url, {
        method: "GET", headers: { Authorization: `Bearer ${token}` },
        credentials: "omit", cache: "no-store", signal: controller.signal,
      });
      if (!isCurrent()) throw new Error("Session changed");
      if (!response.ok) throw new Error("Access could not be verified");
      const payload = await response.json();
      if (!isCurrent()) throw new Error("Session changed");
      if (!payload || typeof payload !== "object" || payload.error ||
        ["ok", "authorized", "authenticated", "isAuthenticated"].some((key) => payload[key] === false)) {
        throw new Error("Access could not be verified");
      }
      return payload;
    };
    const verify = async () => {
      if (!isCurrent()) {
        setResult({ epoch, error: !providerPending });
        return;
      }
      try {
        const endpoint = portal === "client"
          ? process.env.REACT_APP_AUTH_CHECK_ENDPOINT || "/api/auth/client-auth-check"
          : "/api/auth/superadmin-auth-check";
        const authUrl = /^https?:\/\//.test(endpoint) ? endpoint : `${authBase(portal)}/${endpoint.replace(/^\//, "").replace(/^(?!api\/)/, "api/")}`;
        const identity = await read(authUrl);
        const role = normalizeRole(identity.role || identity.user?.role);
        const accountId = normalizeId(identity.user?.id ?? identity.user?._id ?? identity.accountId ?? identity.userId ?? identity.id);
        if (!accountId || (portal === "superadmin" ? role !== "superadmin" : !["client", "creative"].includes(role))) {
          throw new Error("Account identity could not be verified");
        }
        const base = normalizeBase(apiBase) || authBase(portal);
        let verifiedAdvertiser = requestedAdvertiser;
        let authorizedCampaign = null;
        if (requestedCampaign) {
          const payload = await read(`${base}/api/media-campaigns/${encodeURIComponent(requestedCampaign)}`);
          const campaign = payload.campaign || payload;
          authorizedCampaign = campaign;
          if (normalizeId(campaign.id) !== requestedCampaign) throw new Error("Campaign could not be verified");
          verifiedAdvertiser = normalizeId(campaign.advertiser_id ?? campaign.advertiserId ?? campaign.advertiser?.id);
          if (!verifiedAdvertiser) throw new Error("Campaign advertiser could not be verified");
        }
        if (verifiedAdvertiser) {
          // This endpoint enforces the current token's advertiser permission,
          // including empty-draft advertisers. Never infer it from old storage.
          const drafts = await read(`${base}/api/sherloc/media-planner/drafts?advertiserId=${encodeURIComponent(verifiedAdvertiser)}`);
          if (!Array.isArray(drafts.drafts)) throw new Error("Advertiser access could not be verified");
        }
        if (!isCurrent()) return;
        setResult({ epoch, session: {
          key: `${portal}:${accountId}:${verifiedAdvertiser}:${epoch.revision}`,
          accountId, advertiserId: verifiedAdvertiser, campaignId: requestedCampaign,
          token, isCurrent, authorizedCampaign,
        } });
      } catch {
        if (isCurrent()) setResult({ epoch, error: true });
      }
    };
    verify();
    return () => { cancelled = true; controller.abort(); };
  }, [epoch, portal, token, requestedAdvertiser, requestedCampaign, apiBase, providerBlocked, providerPending]);

  if (result?.epoch === epoch && result.session?.isCurrent()) {
    return <React.Fragment key={result.session.key}>{children(result.session)}</React.Fragment>;
  }
  if (result?.epoch === epoch && result.error) {
    return <div role="alert">
      <p>Planner access could not be verified. Retry, select an authorized advertiser, or sign in again.</p>
      <button type="button" onClick={() => {
        epoch.revoked = true;
        setRefresh((value) => value + 1);
      }}>Retry</button>
    </div>;
  }
  return <div role="status">Verifying planner access…</div>;
}
